if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.intersectingTargets = new Set();
        this.onStickyClick = this.onStickyClick.bind(this);
        this.onMediaChange = this.onMediaChange.bind(this);
        this.syncFromMainButton = this.syncFromMainButton.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onOptionChange = this.onOptionChange.bind(this);
        this.onScrollOrResize = this.onScrollOrResize.bind(this);
      }

      connectedCallback() {
        // Escape Dawn scroll-trigger transforms — they break position:fixed
        const sectionId = this.dataset.section;
        document.querySelectorAll(`body > sticky-add-to-cart[data-section="${sectionId}"]`).forEach((el) => {
          if (el !== this) el.remove();
        });

        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }

        if (this._initialized) return;
        this._initialized = true;

        this.sectionId = sectionId;
        this.button = this.querySelector('[data-sticky-add-to-cart]');
        this.labelText = this.querySelector('[data-sticky-text]');
        this.labelPrice = this.querySelector('[data-sticky-price]');
        this.labelSeparator = this.querySelector('[data-sticky-separator]');
        this.optionsContainer = this.querySelector('[data-sticky-options]');
        this.mediaQuery = window.matchMedia('(max-width: 749px)');

        this.resolveProductNodes();

        if (!this.button || !this.mainButton || !this.formButtons) return;

        this.button.addEventListener('click', this.onStickyClick);
        this.mediaQuery.addEventListener('change', this.onMediaChange);
        window.addEventListener('scroll', this.onScrollOrResize, { passive: true });
        window.addEventListener('resize', this.onScrollOrResize);

        this.setupMainButtonObserver();
        this.setupVariantListener();
        this.setupOptionListener();
        this.buildSizeOptions();
        this.copyColorSchemeFromProduct();
        this.onMediaChange();
        this.syncFromMainButton();
        this.removeAttribute('hidden');
      }

      disconnectedCallback() {
        if (this.parentElement === document.body) return;

        this.button?.removeEventListener('click', this.onStickyClick);
        this.mediaQuery?.removeEventListener('change', this.onMediaChange);
        window.removeEventListener('scroll', this.onScrollOrResize);
        window.removeEventListener('resize', this.onScrollOrResize);
        this.observer?.disconnect();
        this.mainButtonObserver?.disconnect();
        this.variantSelects?.removeEventListener('change', this.onOptionChange);
        if (this.unsubscribeVariant) this.unsubscribeVariant();
        this._initialized = false;
      }

      resolveProductNodes() {
        const root =
          document.querySelector(`product-info[data-section="${this.sectionId}"]`) ||
          document.querySelector(`#ProductInfo-${this.sectionId}`) ||
          document.querySelector('product-info') ||
          document;

        this.productForm =
          root.querySelector(`product-form[data-section-id="${this.sectionId}"]`) ||
          root.querySelector('product-form') ||
          document.querySelector('product-form');

        this.form = this.productForm?.querySelector('form[data-type="add-to-cart-form"]');
        this.mainButton = this.productForm?.querySelector('[type="submit"].product-form__submit');
        this.formButtons =
          this.productForm?.querySelector('[data-form-buttons]') ||
          document.querySelector('[data-form-buttons]');
      }

      copyColorSchemeFromProduct() {
        const source =
          this.mainButton ||
          this.productForm?.closest('[class*="color-"]') ||
          this.formButtons?.closest('.gradient') ||
          document.querySelector('.product');

        if (!source) return;

        const styles = getComputedStyle(source);
        [
          '--color-button',
          '--color-button-text',
          '--alpha-button-background',
          '--color-base-accent-1',
          '--color-base-solid-button-labels',
        ].forEach((prop) => {
          const value = styles.getPropertyValue(prop);
          if (value) this.style.setProperty(prop, value.trim());
        });

        this.syncButtonAppearance();
      }

      syncButtonAppearance() {
        if (!this.mainButton || !this.button) return;

        const styles = getComputedStyle(this.mainButton);
        this.button.style.backgroundColor = styles.backgroundColor;
        this.button.style.color = styles.color;
        this.button.style.borderColor = styles.borderColor;
        this.button.style.borderRadius = styles.borderRadius;
        this.button.style.fontSize = styles.fontSize;
        this.button.style.fontWeight = styles.fontWeight;
        this.button.style.letterSpacing = styles.letterSpacing;
        this.button.style.textTransform = styles.textTransform;
        this.button.style.boxShadow = 'none';
      }

      onScrollOrResize() {
        if (!this.mediaQuery?.matches) return;
        this.resolveProductNodes();
        this.updateVisibilityFromRects();
      }

      onMediaChange() {
        if (this.mediaQuery.matches) {
          this.resolveProductNodes();
          this.setupIntersectionObserver();
          this.updateVisibilityFromRects();
        } else {
          this.observer?.disconnect();
          this.observer = null;
          this.intersectingTargets.clear();
          this.classList.remove('is-active', 'is-options-visible');
        }
      }

      setupIntersectionObserver() {
        this.observer?.disconnect();
        this.intersectingTargets.clear();

        if (!this.formButtons) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.intersectingTargets.add(entry.target);
              } else {
                this.intersectingTargets.delete(entry.target);
              }
            });
            this.updateVisibility();
          },
          { root: null, threshold: 0 }
        );

        this.observer.observe(this.formButtons);
        this.updateVisibilityFromRects();
      }

      updateVisibilityFromRects() {
        if (!this.mediaQuery.matches) return;
        if (!this.formButtons) {
          this.resolveProductNodes();
        }
        if (!this.formButtons) return;

        const rect = this.formButtons.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0 && rect.height > 0;

        this.intersectingTargets.clear();
        if (inView) this.intersectingTargets.add(this.formButtons);

        this.updateVisibility();
      }

      updateVisibility() {
        if (!this.mediaQuery.matches) return;

        // Visible whenever main ATC is off-screen — including over the footer
        const shouldShow = this.intersectingTargets.size === 0;
        this.classList.toggle('is-active', shouldShow);

        if (!shouldShow) {
          this.classList.remove('is-options-visible');
        }
      }

      setupMainButtonObserver() {
        if (!this.mainButton) return;
        this.mainButtonObserver?.disconnect();
        this.mainButtonObserver = new MutationObserver(this.syncFromMainButton);
        this.mainButtonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
        });
      }

      setupVariantListener() {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }
      }

      setupOptionListener() {
        this.variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
        this.variantSelects?.addEventListener('change', this.onOptionChange);
      }

      onOptionChange() {
        this.buildSizeOptions();
        this.syncFromMainButton();
      }

      onVariantChange(event) {
        if (event?.data?.sectionId && event.data.sectionId !== this.sectionId) return;

        this.resolveProductNodes();
        this.setupMainButtonObserver();

        const variant = event?.data?.variant;
        if (variant && this.labelPrice && typeof variant.price === 'number') {
          this.labelPrice.textContent = this.formatMoney(variant.price);
        }

        this.syncFromMainButton();
        this.buildSizeOptions();
        if (this.mediaQuery.matches) {
          this.setupIntersectionObserver();
          this.updateVisibilityFromRects();
        }
      }

      syncFromMainButton() {
        if (!this.mainButton || !this.button) return;

        const mainTextEl = this.mainButton.querySelector('span:not(.sold-out-message)');
        const mainText = (mainTextEl?.textContent || '').trim();
        if (mainText && this.labelText) {
          this.labelText.textContent = mainText.toUpperCase();
        }

        const isDisabled =
          this.mainButton.hasAttribute('disabled') ||
          this.mainButton.getAttribute('aria-disabled') === 'true';
        this.button.disabled = isDisabled;

        const priceContainer = document.querySelector(
          `#price-${this.sectionId} .price-item--sale, #price-${this.sectionId} .price-item--regular`
        );
        if (priceContainer && this.labelPrice) {
          const priceText = priceContainer.textContent.replace(/\s+/g, ' ').trim();
          if (priceText) this.labelPrice.textContent = priceText;
        }

        const showPrice =
          Boolean(this.labelPrice?.textContent?.trim()) &&
          !/sold out|unavailable|agotado|no disponible|agota/i.test(mainText);

        if (this.labelPrice) this.labelPrice.hidden = !showPrice;
        if (this.labelSeparator) this.labelSeparator.hidden = !showPrice;

        this.syncButtonAppearance();
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) {
          try {
            return window.Shopify.formatMoney(
              cents,
              window.theme?.moneyFormat || undefined
            );
          } catch (e) {
            // fall through
          }
        }
        if (typeof cents !== 'number') return '';
        try {
          return (cents / 100).toLocaleString(undefined, {
            style: 'currency',
            currency: window.Shopify?.currency?.active || 'EUR',
          });
        } catch (e) {
          return (cents / 100).toFixed(2);
        }
      }

      getSizeFieldset() {
        const variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
        if (!variantSelects) return null;

        const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset.product-form__input'));

        const byName = fieldsets.find((fieldset) => {
          const name = (fieldset.querySelector('legend, .form__label')?.textContent || '').toLowerCase();
          return /size|talla|taille|größe|groesse|maß|maat|taglia/.test(name);
        });
        if (byName) return byName;

        const byGrid = fieldsets.find((fieldset) => fieldset.classList.contains('custom-grid'));
        if (byGrid) return byGrid;

        return fieldsets.find((fieldset) => {
          const name = (fieldset.querySelector('legend, .form__label')?.textContent || '').toLowerCase();
          return !/color|colour|couleur|kolore|colore/.test(name);
        });
      }

      buildSizeOptions() {
        if (!this.optionsContainer) return;

        const sizeFieldset = this.getSizeFieldset();
        if (!sizeFieldset) {
          this.optionsContainer.innerHTML = '';
          return;
        }

        const inputs = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
        if (!inputs.length) {
          this.optionsContainer.innerHTML = '';
          return;
        }

        this.optionsContainer.innerHTML = '';

        inputs.forEach((input) => {
          const optionButton = document.createElement('button');
          optionButton.type = 'button';
          optionButton.className = 'product-sticky-atc__option';
          optionButton.textContent = (input.value || '').trim();
          optionButton.dataset.value = input.value;

          if (input.checked) optionButton.classList.add('is-selected');
          if (input.classList.contains('disabled') || input.disabled) {
            optionButton.classList.add('is-disabled');
          }

          optionButton.addEventListener('click', () => {
            if (optionButton.classList.contains('is-disabled')) return;
            input.click();
            this.classList.remove('is-options-visible');
            this.buildSizeOptions();
            this.syncFromMainButton();
          });

          this.optionsContainer.appendChild(optionButton);
        });
      }

      hasSelectedAvailableSize() {
        const sizeFieldset = this.getSizeFieldset();
        if (!sizeFieldset) return true;

        const selected = sizeFieldset.querySelector('input[type="radio"]:checked');
        if (!selected) return false;
        return !selected.classList.contains('disabled') && !selected.disabled;
      }

      onStickyClick(event) {
        event.preventDefault();
        if (this.button.disabled) return;

        this.resolveProductNodes();

        const sizeFieldset = this.getSizeFieldset();
        const sizeCount = sizeFieldset
          ? sizeFieldset.querySelectorAll('input[type="radio"]').length
          : 0;

        if (sizeCount > 1 && !this.hasSelectedAvailableSize()) {
          this.classList.add('is-options-visible');
          return;
        }

        this.classList.remove('is-options-visible');

        if (!this.form || !this.mainButton) return;

        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit(this.mainButton);
        } else {
          this.mainButton.click();
        }
      }
    }
  );
}
